import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ViewProps, useWindowDimensions } from 'react-native';
import { WebView } from 'react-native-webview';

interface InvoicePreviewProps {
  htmlContent: string;
  preview?: boolean;
  scale?: number;
  style?: ViewProps['style'];
  onFieldClick?: (fieldName: string) => void;
}

const InvoicePreview = forwardRef<WebView, InvoicePreviewProps>(
  ({ htmlContent, preview, scale, style, onFieldClick }: InvoicePreviewProps, ref) => {
    const { width } = useWindowDimensions();
    const webViewRef = useRef<WebView>(null);
    const previousContentRef = useRef<string>('');
    const [webViewKey, setWebViewKey] = useState(0);
    const [webViewSource, setWebViewSource] = useState({ html: htmlContent });
    
    // Store onFieldClick in a ref so it's accessible from the WebView context
    const onFieldClickRef = useRef(onFieldClick);
    useEffect(() => {
      onFieldClickRef.current = onFieldClick;
    }, [onFieldClick]);
    
    // Combine refs
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(webViewRef.current);
      } else if (ref) {
        ref.current = webViewRef.current;
      }
    }, [ref]);
    
    // Reload WebView when htmlContent changes
    useEffect(() => {
      if (htmlContent !== previousContentRef.current) {
        previousContentRef.current = htmlContent;
        // Update the source to trigger WebView reload
        setWebViewSource({ html: htmlContent });
        // Force re-render by updating the key - this will completely remount the WebView
        setWebViewKey(prev => prev + 1);
      }
    }, [htmlContent]);

    // Listen for window messages and custom events on web platform
    useEffect(() => {
      if (Platform.OS === 'web' && onFieldClick) {
        const handleWindowMessage = (event: MessageEvent) => {
          try {
            // Only process messages that look like our field click messages
            if (typeof event.data === 'string') {
              // Check if it's our FIELD_CLICK message - be more specific
              const trimmed = event.data.trim();
              if (trimmed.startsWith('{"type":"FIELD_CLICK"') && trimmed.includes('"fieldName"')) {
                const data = JSON.parse(event.data);
                if (data && typeof data === 'object' && data.type === 'FIELD_CLICK' && data.fieldName) {
                  console.log('🌐 Window message received for field:', data.fieldName);
                  onFieldClick(data.fieldName);
                }
              }
            }
          } catch (error) {
            // Ignore parsing errors for non-our messages
          }
        };

        const handleCustomEvent = (event: CustomEvent) => {
          try {
            if (event.detail && event.detail.type === 'FIELD_CLICK' && event.detail.fieldName) {
              console.log('🎯 CustomEvent received for field:', event.detail.fieldName);
              onFieldClick(event.detail.fieldName);
            }
          } catch (error) {
            console.error('Error handling custom event:', error);
          }
        };

        // Listen on window for postMessage events from the WebView iframe
        window.addEventListener('message', handleWindowMessage);
        window.addEventListener('invoiceFieldClick', handleCustomEvent as EventListener);
        document.addEventListener('invoiceFieldClick', handleCustomEvent as EventListener);
        
        // Also try to find the WebView iframe and listen to it directly
        const findWebViewIframe = () => {
          const iframes = document.querySelectorAll('iframe');
          iframes.forEach((iframe) => {
            try {
              // Check if this iframe contains our invoice preview
              if (iframe.contentWindow) {
                // The iframe's contentWindow should handle messages internally
                // Messages from iframe will bubble to window
              }
            } catch (e) {
              // Cross-origin restrictions - that's fine
            }
          });
        };
        
        // Try to find iframe after a delay
        setTimeout(findWebViewIframe, 1000);
        
        return () => {
          window.removeEventListener('message', handleWindowMessage);
          window.removeEventListener('invoiceFieldClick', handleCustomEvent as EventListener);
          document.removeEventListener('invoiceFieldClick', handleCustomEvent as EventListener);
        };
      }
    }, [onFieldClick]);
    
    // For react-native-webview, we handle responsive content through viewportContent
    const injectedJavaScript = `
      (function() {
        // Set viewport
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=${width * 1.2}, initial-scale=1.0, maximum-scale=1.0');
        } else {
          const meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content = 'width=${width * 1.2}, initial-scale=1.0, maximum-scale=1.0';
          document.getElementsByTagName('head')[0].appendChild(meta);
        }

        // Make fields clickable and send messages to React Native
        function makeFieldsClickable() {
          // Find all elements with data-field attribute
          const fields = document.querySelectorAll('[data-field]');
          
          fields.forEach(function(field) {
            // Remove existing click handlers to avoid duplicates
            field.removeEventListener('click', handleFieldClick);
            // Add click handler
            field.addEventListener('click', handleFieldClick);
            // Add visual feedback
            field.style.cursor = 'pointer';
            field.style.transition = 'background-color 0.2s';
            
            // Add hover effect
            field.addEventListener('mouseenter', function() {
              this.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
            });
            field.addEventListener('mouseleave', function() {
              this.style.backgroundColor = '';
            });
          });
        }

        // Store the last clicked field for the return value mechanism
        window.__lastInvoiceFieldClick = null;
        
        function handleFieldClick(event) {
          event.preventDefault();
          event.stopPropagation();
          
          const fieldName = this.getAttribute('data-field');
          console.log('🔵 Field clicked in WebView:', fieldName);
          
          if (!fieldName) {
            console.warn('⚠️ No data-field attribute found on clicked element');
            return;
          }
          
          // Store the field name for the return value mechanism
          window.__lastInvoiceFieldClick = fieldName;
          
          // Send message to React Native
          const message = JSON.stringify({
            type: 'FIELD_CLICK',
            fieldName: fieldName
          });
          console.log('🔵 Prepared message:', message);
          
          // Try ReactNativeWebView first (works on native and some web setups)
          if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
            console.log('🔵 Using window.ReactNativeWebView.postMessage');
            try {
              window.ReactNativeWebView.postMessage(message);
              console.log('✅ Message sent via ReactNativeWebView');
            } catch (e) {
              console.error('❌ Error sending via ReactNativeWebView:', e);
            }
          }
          
          // For web, try to dispatch a custom event that the parent can listen to
          if (window.dispatchEvent) {
            console.log('🔵 Trying CustomEvent dispatch');
            try {
              const customEvent = new CustomEvent('invoiceFieldClick', {
                detail: { type: 'FIELD_CLICK', fieldName: fieldName },
                bubbles: true,
                cancelable: true
              });
              window.dispatchEvent(customEvent);
              document.dispatchEvent(customEvent);
              console.log('✅ CustomEvent dispatched');
            } catch (e) {
              console.error('❌ Error dispatching CustomEvent:', e);
            }
          }
          
          // Also try window.postMessage for web compatibility (sends to parent)
          if (window.postMessage) {
            console.log('🔵 Trying window.postMessage to parent');
            try {
              if (window.parent && window.parent !== window) {
                window.parent.postMessage(message, '*');
                console.log('✅ Message sent via window.parent.postMessage');
              } else {
                window.postMessage(message, '*');
                console.log('✅ Message sent via window.postMessage');
              }
            } catch (e) {
              console.error('❌ Error sending via postMessage:', e);
            }
          }
        }
        
        // Poll for field clicks and return them via injectedJavaScript return value
        setInterval(function() {
          if (window.__lastInvoiceFieldClick) {
            const fieldName = window.__lastInvoiceFieldClick;
            window.__lastInvoiceFieldClick = null;
            // This will be caught by onMessage when injectedJavaScript returns
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'FIELD_CLICK',
                fieldName: fieldName
              }));
            }
          }
        }, 100);

        // Initialize on load
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', makeFieldsClickable);
        } else {
          makeFieldsClickable();
        }

        // Re-initialize after a short delay to catch dynamically added content
        setTimeout(makeFieldsClickable, 100);
        setTimeout(makeFieldsClickable, 500);
        
        return true;
      })();
    `;
    
    // Create a unique key based on content hash to force re-render when content changes
    // Use a more robust hash that includes content length and a hash of the content
    const contentHash = htmlContent.length + '-' + 
      htmlContent.substring(0, 200).split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0);
    
    // Handle messages from WebView
    const handleMessage = useCallback((event: any) => {
      try {
        // On web, messages come via event.data, on native via event.nativeEvent.data
        let messageData: string | undefined;
        
        if (Platform.OS === 'web') {
          // Try event.data first (for window.postMessage), then event.nativeEvent.data (for WebView messages)
          messageData = event.data || event.nativeEvent?.data;
        } else {
          messageData = event.nativeEvent?.data;
        }
        
        // Skip if no message data
        if (!messageData) {
          return;
        }
        
        // Skip if message is not a string
        if (typeof messageData !== 'string') {
          return;
        }
        
        // Only process messages that look like our FIELD_CLICK messages
        if (!messageData.trim().startsWith('{"type":"FIELD_CLICK"')) {
          return;
        }
        
        console.log('📨 WebView FIELD_CLICK message received:', messageData);
        
        const data = JSON.parse(messageData);
        
        // Verify it's our message structure
        if (data && typeof data === 'object' && data.type === 'FIELD_CLICK' && data.fieldName) {
          console.log('🎯 Calling onFieldClick with:', data.fieldName);
          if (onFieldClick) {
            onFieldClick(data.fieldName);
            console.log('✅ onFieldClick called successfully');
          } else {
            console.warn('⚠️ onFieldClick is not defined');
          }
        }
      } catch (error) {
        // Only log errors for messages that look like they might be ours
        if (event.nativeEvent?.data?.includes('FIELD_CLICK') || event.data?.includes('FIELD_CLICK')) {
          console.error('❌ Error parsing FIELD_CLICK message:', error);
        }
      }
    }, [onFieldClick]);

    return (
      <WebView
        key={`${webViewKey}-${contentHash}`} // Force re-render when htmlContent changes
        ref={webViewRef}
        source={webViewSource}
        injectedJavaScript={injectedJavaScript} // Handle responsive content through viewport manipulation
        onMessage={handleMessage}
        javaScriptEnabled={true}
        style={[
          {
            flex: 1,
            minWidth: "120%",
            left: -50,
            transform: [{ scale: scale || undefined }],
            borderRadius: 5,
            shadowRadius: 20,
            shadowOpacity: 0.2,
            // Remove resizeMode as it's not valid for WebView style
            overflow: "visible",
          },
          style,
        ]}
        allowUniversalAccessFromFileURLs={true}
        scrollEnabled={Platform.OS === 'web' ? false : true}
        scalesPageToFit={Platform.OS === 'web' ? true : false}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        allowFileAccess={true}
        allowingReadAccessToURL="*" // for iOS
        allowFileAccessFromFileURLs={true}
      />
    );
  }
);

export default InvoicePreview;
